import { cn } from '@/_shared/lib/utils';

/**
 * A small icon + truncating label for a list row/card's meta line (schedule, source, panel count, …).
 * All-phrasing (`<span>`s) so it stays valid inside the table's row-activator button.
 *
 * `icon` is typed as "anything that takes a className", not `LucideIcon`, so a brand mark can sit in the
 * chip too — the activations list puts the destination's platform glyph beside its account, and those
 * marks are `<img>`s (see `audiences/icons/DestinationTypeIcon`), not Lucide components. The glyph is
 * hidden from assistive tech by a wrapper rather than by an `aria-hidden` prop on the component, so the
 * type stays the minimum a caller has to satisfy and every icon is decorative by construction.
 */
export function MetaChip({
  icon: Icon,
  className,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  className?: string;
  /**
   * The full value, for a chip whose label can be clipped. It sits on the truncating span rather than
   * the wrapper because that is the element the ellipsis appears on, so hover lands where the text ran
   * out. Optional: a chip that cannot clip should not advertise a tooltip it does not need.
   */
  title?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1', className)}>
      <span aria-hidden className="flex shrink-0 items-center">
        <Icon className="size-3.5 shrink-0" />
      </span>
      <span title={title} className="truncate">
        {children}
      </span>
    </span>
  );
}
